import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import { apiFetch, clearStoredToken, getStoredToken } from './apiClient';
import Login from './pages/Login';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import Sites from './pages/Sites';
import MotsCles from './pages/MotsCles';
import Sujets from './pages/Sujets';
import Contenus from './pages/Contenus';
import SeoAnalyse from './pages/SeoAnalyse';
import Parametres from './pages/Parametres';
import Veille from './pages/Veille';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'sites', label: 'Sites' },
  { id: 'motscles', label: 'Mots-clés' },
  { id: 'sujets', label: 'Sujets' },
  { id: 'contenus', label: 'Contenus' },
  { id: 'seo-analyse', label: 'SEO Analyse' },
  { id: 'parametres', label: 'Paramètres' },
  { id: 'veille', label: 'Veille' },
];

function App() {
  const [authState, setAuthState] = useState({ status: 'checking' });
  const [activePage, setActivePage] = useState('dashboard');
  const [backendStatus, setBackendStatus] = useState({
    state: 'loading',
    label: 'Connexion au backend...',
  });
  const [dashboardData, setDashboardData] = useState({
    totalSites: 0,
    totalArticlesGeneres: 0,
    totalArticlesPublies: 0,
    totalMotscles: 0,
    totalSujetsValides: 0,
    articlesCeMois: 0,
    activite30j: [],
    activiteRecente: [],
  });
  const [dashboardLoading, setDashboardLoading] = useState(true);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    []
  );

  const statsCards = useMemo(
    () => [
      { label: 'Sites actifs', value: dashboardData.totalSites },
      { label: 'Mots-clés actifs', value: dashboardData.totalMotscles },
      { label: 'Sujets en attente', value: dashboardData.totalSujetsValides },
      { label: 'Articles générés', value: dashboardData.totalArticlesGeneres },
      { label: 'Articles publiés', value: dashboardData.totalArticlesPublies },
      { label: 'Publiés ce mois', value: dashboardData.articlesCeMois },
    ],
    [dashboardData]
  );

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      setAuthState({ status: 'guest' });
      return;
    }
    (async () => {
      const response = await apiFetch('/api/auth/verify');
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.valid) {
        setAuthState({ status: 'user' });
      } else {
        clearStoredToken();
        setAuthState({ status: 'guest' });
      }
    })();
  }, []);

  useEffect(() => {
    const onLogout = () => setAuthState({ status: 'guest' });
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  useEffect(() => {
    if (authState.status !== 'user') return;
    let mounted = true;

    const checkBackend = async () => {
      try {
        const response = await fetch('/api/health');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!mounted) return;

        setBackendStatus({
          state: 'online',
          label: data?.status || 'Backend connecté',
        });
      } catch (error) {
        if (!mounted) return;
        setBackendStatus({
          state: 'offline',
          label: 'Backend non connecté',
        });
      }
    };

    checkBackend();
    return () => {
      mounted = false;
    };
  }, [authState.status]);

  useEffect(() => {
    if (authState.status !== 'user') return;
    let mounted = true;

    const loadDashboardStats = async () => {
      try {
        setDashboardLoading(true);
        const statsResponse = await apiFetch('/api/stats');
        if (!statsResponse.ok) {
          throw new Error('Erreur récupération stats');
        }
        const statsData = await statsResponse.json();

        const sitesResponse = await apiFetch('/api/sites');
        const sites = sitesResponse.ok ? await sitesResponse.json() : [];
        const activeSites = (Array.isArray(sites) ? sites : []).filter((site) => site?.actif);

        await Promise.all(
          activeSites.map(async (site) => {
            try {
              await apiFetch(`/api/motscles/${site.id}`);
            } catch {
              return null;
            }
            return null;
          })
        );

        if (!mounted) return;

        setDashboardData({
          totalSites: Number(statsData?.total_sites || 0),
          totalArticlesGeneres: Number(statsData?.total_articles_generes || 0),
          totalArticlesPublies: Number(statsData?.total_articles_publies || 0),
          totalMotscles: Number(statsData?.total_motscles || 0),
          totalSujetsValides: Number(statsData?.total_sujets_valides || 0),
          articlesCeMois: Number(statsData?.articles_ce_mois || 0),
          activite30j: Array.isArray(statsData?.activite_30j) ? statsData.activite_30j : [],
          activiteRecente: Array.isArray(statsData?.activite_recente) ? statsData.activite_recente : [],
        });
      } catch {
        if (!mounted) return;
        setDashboardData({
          totalSites: 0,
          totalArticlesGeneres: 0,
          totalArticlesPublies: 0,
          totalMotscles: 0,
          totalSujetsValides: 0,
          articlesCeMois: 0,
          activite30j: [],
          activiteRecente: [],
        });
      } finally {
        if (mounted) setDashboardLoading(false);
      }
    };

    loadDashboardStats();
    return () => {
      mounted = false;
    };
  }, [authState.status]);

  const handleLogout = async () => {
    const token = getStoredToken();
    if (token) {
      try {
        await apiFetch('/api/auth/logout', { method: 'POST' });
      } catch {
        /* ignore */
      }
    }
    clearStoredToken();
    setAuthState({ status: 'guest' });
    setActivePage('dashboard');
  };

  if (authState.status === 'checking') {
    return (
      <div className="login-page login-page--loading">
        <p className="login-loading-text">Chargement...</p>
      </div>
    );
  }

  if (authState.status === 'guest') {
    return <Login onSuccess={() => setAuthState({ status: 'user' })} />;
  }

  const activeNav = NAV_ITEMS.find((item) => item.id === activePage) || NAV_ITEMS[0];

  const renderPage = () => {
    if (activePage === 'sites') {
      return <Sites />;
    }
    if (activePage === 'motscles') {
      return <MotsCles />;
    }
    if (activePage === 'sujets') {
      return <Sujets />;
    }
    if (activePage === 'contenus') {
      return <Contenus />;
    }
    if (activePage === 'seo-analyse') {
      return <SeoAnalyse />;
    }
    if (activePage === 'parametres') {
      return <Parametres />;
    }
    if (activePage === 'veille') {
      return <Veille />;
    }

    if (activePage !== 'dashboard') {
      return (
        <section className="empty-state">
          <div className="empty-state-card">
            <h2>Module en préparation</h2>
            <p>Cette section sera bientôt disponible dans SEO Machine.</p>
          </div>
        </section>
      );
    }

    return (
      <>
        <section className="stats-grid" aria-label="Statistiques">
          {statsCards.map((stat) => (
            <article className="stat-card" key={stat.label}>
              <span className="stat-title">{stat.label}</span>
              <strong className="stat-value">{dashboardLoading ? '...' : stat.value}</strong>
            </article>
          ))}
        </section>

        <section className="dashboard-panels">
          <article className="dashboard-panel">
            <h2>Activité des 30 derniers jours</h2>
            {dashboardData.activite30j.length === 0 ? (
              <p className="dashboard-muted">Aucune publication sur les 30 derniers jours.</p>
            ) : (
              <div className="dashboard-chart-wrap">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={dashboardData.activite30j}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#232334" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#9ba2c4', fontSize: 12 }}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                        })
                      }
                    />
                    <YAxis allowDecimals={false} tick={{ fill: '#9ba2c4', fontSize: 12 }} />
                    <Tooltip
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString('fr-FR', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'long',
                        })
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#6c7cff"
                      strokeWidth={3}
                      dot={{ r: 2 }}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </article>

          <article className="dashboard-panel">
            <h2>Activité récente</h2>
            {dashboardData.activiteRecente.length === 0 ? (
              <p className="dashboard-muted">Aucun article publié récemment.</p>
            ) : (
              <ul className="recent-activity-list">
                {dashboardData.activiteRecente.map((article, index) => (
                  <li key={`${article.titre}-${article.datePublication}-${index}`} className="recent-activity-item">
                    <div>
                      <strong>{article.titre}</strong>
                      <p>{article.site}</p>
                    </div>
                    <span>
                      {article.datePublication
                        ? new Date(article.datePublication).toLocaleDateString('fr-FR')
                        : '-'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        </section>

        {dashboardData.totalSites === 0 && (
          <section className="empty-state">
            <div className="empty-state-card">
              <h2>Aucun site configuré</h2>
              <p>Ajoutez votre premier site pour lancer la production SEO automatisée.</p>
              <button type="button" className="primary-button" onClick={() => setActivePage('sites')}>
                Ajouter un site
              </button>
            </div>
          </section>
        )}
      </>
    );
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">⚡ SEO Machine</div>

        <nav className="nav-menu" aria-label="Navigation principale">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`nav-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => setActivePage(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button type="button" className="sidebar-logout" onClick={handleLogout}>
            Déconnexion
          </button>
          <div className="backend-status" role="status" aria-live="polite">
            <span className={`status-dot ${backendStatus.state}`} />
            <span className="status-label">{backendStatus.label}</span>
          </div>
        </div>
      </aside>

      <main className="dashboard">
        <header className="dashboard-header">
          <h1>{activeNav.label}</h1>
          <p>{today}</p>
        </header>
        {renderPage()}
      </main>
    </div>
  );
}

export default App;